import { KalturaAPIException } from '@kaltura-ott/ott-client-types';
import {
  KalturaLoginResponse,
  KalturaLoginSession,
  KalturaStringValue,
  OttUserAnonymousLoginAction,
  OttUserAnonymousLoginActionArgs,
  OttUserGetAction,
  OttUserLoginAction,
  OttUserLoginActionArgs,
  OttUserLoginWithPinAction,
  OttUserLoginWithPinActionArgs,
  AppTokenAddAction,
  KalturaAppToken,
  AppTokenStartSessionAction,
  AppTokenDeleteAction,
  OttUserLogoutAction,
} from '@kaltura-ott/ott-client-types/api/types';
import { KalturaKeyValue } from '@kaltura-ott/ott-client-types/api/types/KalturaKeyValue';
import { KalturaOTTUser } from '@kaltura-ott/ott-client-types/api/types/KalturaOTTUser';
import { Observable, of, throwError, interval, EMPTY, merge, Subject } from 'rxjs';
import { ajax } from 'rxjs/ajax';
import { catchError, delay, map, switchMap, takeUntil, tap } from 'rxjs/operators';

import { OAuthGetTokenParams, OAuthGetTokenData, AuthGetDeviceData, AuthPinData, ServicesToken } from '../interfaces';

import { tvpilGetLocalCache } from './caching-service';
import { getConfig, KuXConfig } from './config';
import { kalturaClient } from './kaltura-clent';
import { ssoClientInvoke } from './sso-client';

/**
 * Build request payload for service 'ottUser' action 'anonymousLogin'.
 *
 * Usage: Returns tokens (KS and refresh token) for anonymous access
 *
 * @category Authentication
 * @function
 * @param data
 */
export const anonymousLogin = (data: OttUserAnonymousLoginActionArgs): Observable<KalturaLoginSession> => {
  const client = kalturaClient();
  const action = new OttUserAnonymousLoginAction(data);
  return client.request(action);
};

/**
 * Build request payload for service 'ottUser' action 'login'.
 *
 * Usage: login with user name and password
 *
 * @category Authentication
 * @function
 * @param userData:<OttUserLoginActionArgs>
 */
export const userLogin = (userData: OttUserLoginActionArgs): Observable<KalturaLoginResponse> => {
  const client = kalturaClient();
  const action = new OttUserLoginAction(userData);
  return client.request(action).pipe(
    tap((resp) => {
      const cache = tvpilGetLocalCache();
      cache.setItem('login', resp, 4000);
    }),
    catchError((error) => {
      throw error;
    }),
  );
};

interface UserLoginParams {
  [key: string]: KalturaStringValue;
}

/**
 * Build request payload for service 'ottUser' action 'login' with extraParams.
 *
 * Usage: login with user name and password with extraParams
 *
 * @category Authentication
 * @function
 * @param userData:<OttUserLoginActionArgs>
 * @param extraParams: Array<{ [index: number]: { key: string; value: string } }>
 */
export const userLoginWithExtraParams = (
  userData: OttUserLoginActionArgs,
  extraParams: Array<{ key: string; value: string }>,
): Observable<KalturaLoginResponse | KalturaAPIException> => {
  const params: UserLoginParams = {};
  extraParams.forEach((param) => {
    params[param.key] = new KalturaStringValue({ value: param.value });
  });
  const client = kalturaClient();
  const action = new OttUserLoginAction({
    ...userData,
    extraParams: params,
  });
  return client.request(action).pipe(
    catchError((error) => {
      return of(error);
    }),
  );
};

/**
 * Login by AccessToken
 * @param partnerId
 * @param udid
 * @param accessToken
 */
export const loginByAccessToken = (
  partnerId: number,
  udid: string,
  accessToken: string,
): Observable<{ ks: string; username: string; accessToken: string } | KalturaAPIException> => {
  return userLoginWithExtraParams(
    {
      partnerId,
      udid,
      password: '11111',
      username: '11111',
    },
    [
      { key: 'loginType', value: 'loginWithPinOauth2' },
      { key: 'accessToken', value: accessToken },
    ],
  ).pipe(
    switchMap((loginData) => {
      if (loginData instanceof KalturaAPIException) {
        return of(loginData);
      }

      return of({
        ks: loginData.loginSession.ks,
        username: loginData.user.username,
        accessToken,
      });
    }),
    catchError((error) => {
      return of(error);
    }),
  );
};

/**
 * Build request payload for service 'ottUser' action 'get'.
 *
 * Usage: get user data
 *
 * @category Authentication
 * @function
 */
export const getUser = (): Observable<KalturaOTTUser> => {
  try {
    const client = kalturaClient();
    const action = new OttUserGetAction();
    return client.request(action);
  } catch (e) {
    console.trace(e);
    throw e;
  }
};

interface ConfigData {
  authTokenPath: string;
  authDevicePath: string;
  clientId: string;
  plainAuthorizationEndpoint: string;
  scope: string;
  grantType: string;
  responseType: string;
  clientSecret: string;
}

function formatConfigData(): ConfigData {
  return {
    authTokenPath: `${getConfig(KuXConfig)('oAuthEndpoint')}${getConfig(KuXConfig)('authTokenPath')}`,
    authDevicePath: `${getConfig(KuXConfig)('oAuthEndpoint')}${getConfig(KuXConfig)('authDevicePath')}`,
    clientId: getConfig(KuXConfig)('oAuthClientId'),
    plainAuthorizationEndpoint: `${getConfig(KuXConfig)('plainAuthorizationEndpoint')}`,
    scope: getConfig(KuXConfig)('oAuthScope'),
    responseType: getConfig(KuXConfig)('oAuthResponseType'),
    grantType: getConfig(KuXConfig)('oAuthGrantType'),
    clientSecret: getConfig(KuXConfig)('oAuthClientSecret'),
  };
}

/**
 * Get user token after oAuth
 *
 * @category Authentication
 * @function
 * @param parameters: OAuthGetTokenParams
 *
 * @returns {OAuthGetTokenData} Token hash and refresh hash
 */
export function getOAuthToken({ code, redirectUri, codeVerifier }: OAuthGetTokenParams) {
  const { authTokenPath, clientId } = formatConfigData();

  return ajax
    .post(
      authTokenPath,
      new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: clientId,
        code,
        redirect_uri: decodeURIComponent(redirectUri),
        code_verifier: codeVerifier,
      }).toString(),
      { 'Content-Type': 'application/x-www-form-urlencoded' },
    )
    .pipe(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      map(({ response }: any): OAuthGetTokenData => {
        return {
          accessToken: response.access_token,
          refreshToken: response.refresh_token,
        };
      }),
    );
}

/**
 * Perform plain authorization and obtain a token for Services list request
 *
 * @param username
 * @param password
 * @param udid
 * @param service
 *
 * @returns {ServicesToken} jwt and refresh token
 */
export function getServicesTokenByUsernameAndPassword(
  username: string,
  password: string,
  udid: string,
  service: string,
  digSign?: string,
  currentDate?: string,
  clientId?: string,
  plainAuthorizationEndpoint = formatConfigData().plainAuthorizationEndpoint,
): Observable<{ jwt: string; refreshToken: string }> {
  return ajax
    .post(
      plainAuthorizationEndpoint,
      {
        username: username,
        password: password,
        service: service,
        udid: udid,
        ...(digSign ? { tvDeviceId: udid, digSign, clientId, signatureTimestamp: currentDate } : {}),
      },
      { 'Content-Type': 'application/json' },
    )
    .pipe(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      map(({ response }: any): ServicesToken => {
        return {
          jwt: response.jwt,
          refreshToken: response.refresh_token,
        };
      }),
      catchError((error) => {
        return of(error);
      }),
    );
}

/**
 * Get device information by client id
 *
 * @category Authentication
 * @function
 *
 * @returns {AuthGetDeviceData} Device data
 */

export function getAuthDevice(): Observable<AuthGetDeviceData> {
  const { authDevicePath, clientId, scope, responseType } = formatConfigData();

  return ajax
    .post(
      authDevicePath,
      new URLSearchParams({
        client_id: clientId,
        scope: scope,
        response_type: responseType,
      }).toString(),
      { 'Content-Type': 'application/x-www-form-urlencoded' },
    )
    .pipe(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      map(({ response }: any): AuthGetDeviceData => {
        return {
          deviceCode: response.device_code,
          expiresIn: Number(response.expires_in),
          interval: Number(response.interval),
          userCode: response.user_code,
          verificationUri: response.verification_uri,
        };
      }),
      catchError((error) => {
        console.warn(error);
        return throwError(error);
      }),
    );
}

/**
 * Checks Nalog Token by device code
 *
 * @category Authentication
 * @function
 *
 * @returns {AuthPinData} or throws a KalturaAPIException
 */

export function checkNalogToken(code: string): Observable<AuthPinData | undefined> {
  const { authTokenPath, clientId, grantType, clientSecret } = formatConfigData();

  return ajax
    .post(
      authTokenPath,
      new URLSearchParams({
        client_id: clientId,
        grant_type: grantType,
        device_code: code,
        client_secret: clientSecret,
      }).toString(),
      { 'Content-Type': 'application/x-www-form-urlencoded' },
    )
    .pipe(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      map(({ response }: any): AuthPinData | undefined => {
        if (response.error) {
          return;
        }
        return {
          tokenType: response.token_type,
          expiresIn: response.expires_in,
          accessToken: response.access_token,
          refreshToken: response.refresh_token,
        };
      }),
      catchError((error) => {
        console.warn(error);
        return EMPTY;
      }),
    );
}

/**
 * Build request payload for service 'ottUser' action 'loginWithPin'.
 *
 * Usage: login with predefined PIN
 *
 * @category Authentication
 * @function
 * @param userData:<OttUserLoginActionArgs>
 */
export const userLoginWithPin = (userData: OttUserLoginWithPinActionArgs): Observable<KalturaLoginResponse> => {
  const client = kalturaClient();
  const action = new OttUserLoginWithPinAction(userData);
  return client.request(action).pipe(
    catchError((error) => {
      console.warn(error);
      return of(error);
    }),
  );
};

/**
 * Build request payload for service 'AppToken' action 'Add'.
 *
 * Usage: add appToken
 *
 * @category Authentication
 * @function
 */
export const userAppTokenAdd = () => {
  const client = kalturaClient();
  const action = new AppTokenAddAction({
    appToken: new KalturaAppToken(),
  });
  return client.request(action).pipe(
    catchError((error) => {
      console.warn(error);
      return throwError(error);
    }),
  );
};

/**
 * Build request payload for service 'AppToken' action 'Delete'.
 *
 * Usage: remove appToken
 *
 * @category Authentication
 * @function
 */
export const userAppTokenDelete = (id: string) => {
  const client = kalturaClient();
  const action = new AppTokenDeleteAction({
    id,
  });
  return client.request(action).pipe(
    catchError((error) => {
      console.warn(error);
      return throwError(error);
    }),
  );
};

/**
 * Build request payload for service 'AppToken' action 'StartSession'.
 *
 * Usage: start appToken session
 *
 * @category Authentication
 * @function
 */
export const userAppTokenStartSession = ({ id, tokenHash, udid }: { id: string; tokenHash: string; udid: string }) => {
  const client = kalturaClient();
  const action = new AppTokenStartSessionAction({
    id,
    tokenHash,
    udid,
  });
  return client.request(action).pipe(
    catchError((error) => {
      console.warn(error);
      return throwError(error);
    }),
  );
};

/**
 * Build request payload for service 'SsoAdapterProfileInvokeAction' action 'invoke'.
 *
 * Usage: O2 reset password
 *
 * @category Authentication
 * @function
 */
export const o2userResetPassword = (email: string, partnerId: number) => {
  return ssoClientInvoke(partnerId, {
    intent: 'Reset Password',
    adapterData: [
      new KalturaKeyValue({
        key: 'email',
        value: email,
      }),
    ],
  })
    .then(({ message, code, adapterData }) => {
      return {
        message,
        code,
      };
    })
    .catch((error) => {
      return {
        code: error?.code,
        message: error?.message,
      };
    });
};

/**
 * Login mechanism with pin. Will return if login is successful or will be aborted
 * @param partnerId
 * @param udid
 * @param sessionSubject
 * @param setPinData
 */
export function loginByPin(
  partnerId: number,
  udid: string,
  sessionSubject: Subject<unknown>,
  setPinData: (userCode: string, verificationUri: string) => void,
): Observable<{ ks: string; username: string; accessToken: string } | undefined | KalturaAPIException | false> {
  return getAuthDevice().pipe(
    switchMap((deviceData) => {
      const checkIntervalSubject = new Subject();
      const { userCode, verificationUri, deviceCode, interval: intervalTime, expiresIn } = deviceData;
      setPinData(userCode, verificationUri);
      return merge(
        of(true).pipe(
          delay(expiresIn * 1000),
          switchMap(() => {
            checkIntervalSubject.next(null);
            checkIntervalSubject.complete();

            return loginByPin(partnerId, udid, sessionSubject, setPinData);
          }),
        ),
        interval(Number(intervalTime) * 1000).pipe(
          switchMap(() => {
            return checkNalogToken(deviceCode).pipe(
              switchMap((result) => {
                if (!result || !result.accessToken) {
                  return EMPTY;
                }
                const { accessToken } = result;
                return loginByAccessToken(partnerId, udid, accessToken).pipe(
                  catchError((error) => {
                    return of(error);
                  }),
                );
              }),
            );
          }),
          takeUntil(checkIntervalSubject),
        ),
        of(false),
      );
    }),
    takeUntil(sessionSubject),
  );
}

/**
 * Build request payload for service 'ottUser' action 'logout'.
 *
 * Usage: logout ott user
 *
 * @category LogOut
 * @function
 */

export const logoutOttUser = () => {
  const client = kalturaClient();
  const action = new OttUserLogoutAction();
  return client.request(action).pipe(
    catchError((error) => {
      console.warn(error);
      return throwError(error);
    }),
  );
};
